import React, { useState, useCallback } from 'react';
import { useMutation, useQuery } from 'react-query';

import {
  CancelOutlined as CancelIcon,
  Edit as EditIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@material-ui/icons';
import { TableRow, TableCell, IconButton } from '@material-ui/core';

import EditTextInRow from './edit-text-in-row';
import NFT from './nft';
import * as API from 'api';
import { WithTooltip } from 'utils/wrappers';
import MuiGavelIcon from '@mui/icons-material/Gavel';
import MuiIconButton from '@material-ui/core/IconButton';
import MuiMusicOffIcon from '@mui/icons-material/MusicOff';
import MuiEncryptionOffIcon from '@mui/icons-material/NoEncryption';
import MuiMoneyOffIcon from '@mui/icons-material/MoneyOff';
import MuiVerifiedIcon from '@mui/icons-material/Verified';

const { GROUP_RESTRICTION_LEVEL } = require('../../constants/ENUMS.gen.json');

const SubscriptionMemberships = API.SubscriptionMemberships();

const Groups = API.Groups();
const Users = API.Users();

function determineTierText(group) {
  const hasSubscription = !!group.subscriptionMembership;
  const hasWallet = !!group.subscriptionMembership?.wallet_address;
  const isComped = !!group.subscriptionMembership?.comped;
  const tierName = group.subscriptionMembership?.tier;
  const hasDeals = !!group.serviceDeals?.length && group.serviceDeals?.some(deal => deal.is_active);

  //comped or has deals trumps everything
  if (isComped || hasDeals) return 'Premium Services';
  if (tierName) return tierName;
  if (hasWallet && hasSubscription) return 'Genesis'; ///this is no longer correct.
  if (!hasSubscription) return 'No Subscription';
  return 'unknown';
}

export default function ListGroupsTableRowGroup({ group, setSelectedGroup, isExpanded, refetchGroups }) {
  const [editing, setEditing] = useState(null);
  const [isModifyingNFT, setIsModifyingNFT] = useState(false);

  const meQuery = useQuery(['me'], () => Users.getMe());
  const dataMe = meQuery?.data;

  const toggleSubscription = useMutation(
    subscriptionId => {
      const params = subscriptionId ? { subscriptionId } : { groupId: group.id };
      return SubscriptionMemberships.toggleCompedSubscription(params);
    },

    {
      onMutate: () => {
        setIsModifyingNFT(true);
      },

      onSuccess: () => {
        refetchGroups();
        setIsModifyingNFT(false);
      },

      onError: error => {
        alert(error.data?.message || 'Unknown Error');
        toggleSubscription.reset();
        setIsModifyingNFT(false);
      },
    }
  );

  const updateGroup = useMutation(params => Groups.patch(group.id, params), {
    onSuccess: () => {
      refetchGroups();
      setEditing(null);
    },

    onError: error => {
      if (error && error.status === 409) {
        alert('This email address is already associated with a group');
      } else {
        alert(error.data?.message || 'Unknown Error');
      }
      setEditing(null);
      updateGroup.reset();
    },
  });

  function toggleEditingValue(value) {
    setEditing(editing === value ? null : value);
  }

  const tierTxt = determineTierText(group);

  const setGroupRestrictionMutation = useMutation(data => Groups.setGroupRestriction(data.groupId, data.restriction), {
    onSuccess: () => {
      alert('Complete');
      refetchGroups();
      setEditing(null);
    },
    onError: error => {
      alert(error.data?.detail || 'Unknown Error');
    },
  });

  const banGroup = useCallback(
    group => {
      const groupName = prompt(`In order to BAN this group permanently, type "${group.name}" into the box below`);

      if (groupName === group.name) {
        setGroupRestrictionMutation.mutate({ groupId: group.id, restriction: GROUP_RESTRICTION_LEVEL.BANNED });
      }
    },
    [setGroupRestrictionMutation]
  );

  const disableGroupSubscription = useCallback(
    group => {
      const groupName = prompt(
        `In order to DISABLE this group's subscription, type "${group.name}" into the box below`
      );

      if (groupName === group.name) {
        setGroupRestrictionMutation.mutate({ groupId: group.id, restriction: GROUP_RESTRICTION_LEVEL.DISABLE_RENEWAL });
      }
    },
    [setGroupRestrictionMutation]
  );

  const setGroupRestrictionNoConfirmation = useCallback(
    (group, restriction) =>
      setGroupRestrictionMutation.mutate({
        groupId: group.id,
        restriction: restriction,
      }),
    [setGroupRestrictionMutation]
  );

  const SubscriptionModificationGroup = isNFT => (
    <>
      <div style={{ display: 'flex', columnGap: '1rem' }}>
        <WithTooltip tooltip={'No restrictions'}>
          <MuiIconButton onClick={() => setGroupRestrictionNoConfirmation(group, GROUP_RESTRICTION_LEVEL.NONE)}>
            <MuiVerifiedIcon color={group.restriction === GROUP_RESTRICTION_LEVEL.NONE ? 'primary' : 'default'} />
          </MuiIconButton>
        </WithTooltip>
        <WithTooltip tooltip={'Put Group Manual Monetization Policy'}>
          <MuiIconButton
            onClick={() => setGroupRestrictionNoConfirmation(group, GROUP_RESTRICTION_LEVEL.MANUAL_MONETIZATION)}>
            <MuiMoneyOffIcon
              color={group.restriction === GROUP_RESTRICTION_LEVEL.MANUAL_MONETIZATION ? 'error' : 'default'}
            />
          </MuiIconButton>
        </WithTooltip>
        <WithTooltip tooltip={'Put Group In Timeout'}>
          <MuiIconButton
            color={group.restriction === GROUP_RESTRICTION_LEVEL.TIMEOUT ? 'error' : 'default'}
            onClick={() => setGroupRestrictionNoConfirmation(group, GROUP_RESTRICTION_LEVEL.TIMEOUT)}>
            <MuiEncryptionOffIcon color={group.restriction === GROUP_RESTRICTION_LEVEL.TIMEOUT ? 'error' : 'default'} />
          </MuiIconButton>
        </WithTooltip>
        {!isNFT && (
          <>
            <WithTooltip tooltip="Disable Group Subscription">
              <MuiIconButton onClick={() => disableGroupSubscription(group)}>
                <MuiMusicOffIcon
                  color={group.restriction === GROUP_RESTRICTION_LEVEL.DISABLE_RENEWAL ? 'error' : 'default'}
                />
              </MuiIconButton>
            </WithTooltip>
            <WithTooltip tooltip="Ban Group Users">
              <MuiIconButton onClick={() => banGroup(group)}>
                <MuiGavelIcon color={group.restriction === GROUP_RESTRICTION_LEVEL.BANNED ? 'error' : 'default'} />
              </MuiIconButton>
            </WithTooltip>
          </>
        )}
      </div>
    </>
  );

  return (
    <TableRow>
      <TableCell>{group.id}</TableCell>
      <TableCell>
        <IconButton onClick={() => toggleEditingValue('name')}>
          {editing === 'name' ? <CancelIcon /> : <EditIcon />}{' '}
        </IconButton>
        {editing === 'name' ? (
          <EditTextInRow saveValue={updateGroup.mutate} value={group.name} fieldName="name" />
        ) : (
          group.name
        )}
      </TableCell>
      <TableCell>
        <IconButton onClick={() => toggleEditingValue('email')}>
          {editing === 'email' ? <CancelIcon /> : <EditIcon />}{' '}
        </IconButton>
        {editing === 'email' ? (
          <EditTextInRow saveValue={updateGroup.mutate} value={group.email} fieldName="email" />
        ) : (
          group.email
        )}
      </TableCell>
      <TableCell>{tierTxt}</TableCell>
      <TableCell>
        <NFT
          subscription={group.subscriptionMembership}
          toggleSubscription={toggleSubscription.mutate}
          disabled={isModifyingNFT}
          comped={group.subscriptionMembership?.comped}
        />
      </TableCell>
      <TableCell>
        {dataMe?.system_admin
          ? SubscriptionModificationGroup(['Genesis', 'Premium Services'].indexOf(determineTierText(group)) >= 0)
          : null}
      </TableCell>
      <TableCell>
        <IconButton aria-label="expand release" onClick={() => setSelectedGroup(group)} tabIndex={-1}>
          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </TableCell>
    </TableRow>
  );
}
